import React, { useEffect, useRef } from 'react';
import DialogBox from './DialogBox';
import {
  CommandLineIcon,
  BanknotesIcon,
  TableCellsIcon,
  UserPlusIcon,
  PlayIcon,
  MagnifyingGlassIcon,
  ComputerDesktopIcon,
  KeyIcon
} from '@heroicons/react/24/outline';

interface ShortcutItem {
  keys: string;
  description: string;
}

interface ShortcutSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  shortcuts: ShortcutItem[];
}

const Kbd: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <kbd
    className={`inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-md font-mono text-xs font-semibold bg-slate-200 dark:bg-slate-600 text-slate-800 dark:text-slate-100 border border-slate-300 dark:border-slate-500 shadow-sm ${className}`}
  >
    {children}
  </kbd>
);

const ShortcutRow: React.FC<{ keys: string; description: string }> = ({ keys, description }) => {
  const parts = keys.split(/\s*\+\s*/).filter(Boolean);
  return (
    <div className="flex flex-wrap items-center gap-2 py-2 px-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group">
      <div className="flex flex-wrap items-center gap-1">
        {parts.map((part, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="text-slate-400 dark:text-slate-500 text-xs">+</span>}
            <Kbd>{part.trim()}</Kbd>
          </React.Fragment>
        ))}
      </div>
      <span className="text-slate-600 dark:text-slate-300 text-sm flex-1 min-w-0 group-hover:text-slate-900 dark:group-hover:text-slate-100">
        {description}
      </span>
    </div>
  );
};

const defaultSections: ShortcutSection[] = [
  {
    id: 'general',
    title: 'General',
    icon: <ComputerDesktopIcon className="w-5 h-5" />,
    shortcuts: [
      { keys: 'F1', description: 'Open Shortcut Information' },
      { keys: 'Toolbar ?', description: 'Click the help (?) button in the top bar — same as F1' },
      { keys: 'F11', description: 'Toggle fullscreen' },
      { keys: 'Esc', description: 'Close dialog / Cancel' }
    ]
  },
  {
    id: 'games-party',
    title: 'Games & Party',
    icon: <PlayIcon className="w-5 h-5" />,
    shortcuts: [
      { keys: 'Ctrl + Alt + O', description: 'Create new One Day game' },
      { keys: 'Ctrl + Alt + T', description: 'Create new Test game' },
      { keys: 'Ctrl + Alt + S', description: 'Create new Session game' },
      { keys: 'Ctrl + Alt + P', description: 'Create new Party' },
      { keys: 'Ctrl + G', description: 'Create Game (from Game list)' },
      { keys: 'F3', description: 'Search Party (Party list reference)' },
      { keys: 'F4', description: 'Quick Add Party' }
    ]
  },
  {
    id: 'reports',
    title: 'Reports',
    icon: <TableCellsIcon className="w-5 h-5" />,
    shortcuts: [
      { keys: 'Alt + L', description: 'Ledger Account' },
      { keys: 'F2', description: 'Ledger: focus Party Code. Type a code, then Enter to load it' },
      { keys: 'Alt + Shift + H', description: 'Havala Account' },
      { keys: 'Alt + T', description: 'Trial Balance' },
      { keys: 'Ctrl + Alt + Shift + H', description: 'Havala Report' },
      { keys: 'Ctrl + Alt + Shift + R', description: 'Select Match Report' }
    ]
  },
  {
    id: 'havala',
    title: 'Havala',
    icon: <BanknotesIcon className="w-5 h-5" />,
    shortcuts: [
      { keys: 'Alt + H', description: 'Add Havala — works from all pages, including the Havala page' },
      { keys: 'Alt + M', description: 'Create Havala side drawer' },
      { keys: 'Alt + K', description: 'Add Auto Havala (Oneday / Test / Session)' },
      { keys: 'Alt + J', description: 'Add Manual Havala (Oneday / Test / Session)' }
    ]
  },
  {
    id: 'in-game',
    title: 'In-Game (Soda)',
    icon: <CommandLineIcon className="w-5 h-5" />,
    shortcuts: [
      { keys: 'Alt + F7', description: 'Running Match Soda by Party' },
      { keys: 'F9', description: 'Scroll to cut entry (above team summary) — One Day / Test board when scissors show' },
      { keys: 'Alt + F12', description: 'Match Over (Oneday / Test / Session)' },
      { keys: 'Alt + P', description: 'Create new Party' },
      { keys: 'Alt + Shift + P', description: 'View all Party' },
      { keys: 'Alt + Z', description: 'First nation with K (Oneday / Test)' },
      { keys: 'Alt + X', description: 'First nation with L (Oneday / Test)' },
      { keys: 'Alt + C', description: 'Second nation with K (Oneday / Test)' },
      { keys: 'Alt + V', description: 'Second nation with L (Oneday / Test)' }
    ]
  },
  {
    id: 'utility',
    title: 'Utility',
    icon: <UserPlusIcon className="w-5 h-5" />,
    shortcuts: [
      { keys: 'Alt + Q', description: 'Save Party details in Quick Add Party' }
    ]
  }
];

/** Partner role: Header disables most keys; list only what works */
const partnerShortcutSections: ShortcutSection[] = [
  defaultSections[0],
  {
    id: 'partner-enabled',
    title: 'Partner (enabled)',
    icon: <CommandLineIcon className="w-5 h-5" />,
    shortcuts: [
      { keys: 'F3', description: 'Party list reference' },
      { keys: 'Alt + F7', description: 'Running Match Soda by Party' }
    ]
  }
];

/** Admin: Utility-only UI; shortcuts are disabled except F11 (handled in Header). */
const adminShortcutSections: ShortcutSection[] = [
  {
    id: 'general',
    title: 'General',
    icon: <ComputerDesktopIcon className="w-5 h-5" />,
    shortcuts: [
      { keys: 'F11', description: 'Toggle fullscreen' },
      { keys: 'Esc', description: 'Close dialog / Cancel' },
    ],
  },
];

interface ShortcutInformationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Optional: show only sections relevant for Partner role */
  isPartner?: boolean;
  /** Optional: show only sections relevant for Admin (Utility-only toolbar) */
  isAdmin?: boolean;
}

const ShortcutInformationDialog: React.FC<ShortcutInformationDialogProps> = ({
  isOpen,
  onClose,
  isPartner = false,
  isAdmin = false,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }
      const scroller = scrollRef.current;
      if (!scroller) return;
      const page = Math.max(scroller.clientHeight - 24, 48);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        scroller.scrollBy({ top: 40 });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        scroller.scrollBy({ top: -40 });
      } else if (e.key === 'PageDown') {
        e.preventDefault();
        e.stopPropagation();
        scroller.scrollBy({ top: page });
      } else if (e.key === 'PageUp') {
        e.preventDefault();
        e.stopPropagation();
        scroller.scrollBy({ top: -page });
      } else if (e.key === 'Home') {
        e.preventDefault();
        e.stopPropagation();
        scroller.scrollTop = 0;
      } else if (e.key === 'End') {
        e.preventDefault();
        e.stopPropagation();
        scroller.scrollTop = scroller.scrollHeight;
      }
    };
    window.addEventListener('keydown', handleKey, true);
    return () => window.removeEventListener('keydown', handleKey, true);
  }, [isOpen, onClose]);

  const sections = isPartner ? partnerShortcutSections : isAdmin ? adminShortcutSections : defaultSections;
  const leftSections = sections.filter((_, index) => index % 2 === 0);
  const rightSections = sections.filter((_, index) => index % 2 === 1);

  const renderSection = (section: ShortcutSection) => (
    <section
      key={section.id}
      className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/50 overflow-hidden shadow-sm"
    >
      <div className="flex items-center gap-2 px-4 py-3 bg-slate-100/80 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-700">
        <span className="text-indigo-600 dark:text-indigo-400">{section.icon}</span>
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
          {section.title}
        </h3>
      </div>
      <div className="divide-y divide-slate-100 dark:divide-slate-800">
        {section.shortcuts.map((item, idx) => (
          <ShortcutRow key={idx} keys={item.keys} description={item.description} />
        ))}
      </div>
    </section>
  );

  return (
    <DialogBox
      isOpen={isOpen}
      onClose={onClose}
      title="Shortcut Information"
      panelClassName="w-[min(68rem,calc(100vw-1.5rem))] max-w-[68rem]"
      panelStyle={{ maxHeight: '90vh', width: 'min(68rem, calc(100vw - 1.5rem))' }}
      initialFocus={scrollRef}
    >
      <div ref={panelRef} className="flex flex-col max-h-[85vh] overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-indigo-50 to-slate-50 dark:from-slate-800 dark:to-slate-900">
          <KeyIcon className="w-6 h-6 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {isAdmin ? (
              <>Minimal shortcuts apply to your role. Press <Kbd>F11</Kbd> for fullscreen.</>
            ) : (
              <>
                Use these shortcuts to work faster. Press <Kbd>F1</Kbd> or click the toolbar <Kbd>?</Kbd> help button to open this page.
              </>
            )}
          </p>
        </div>

        <div
          ref={scrollRef}
          tabIndex={-1}
          className="min-h-0 flex-1 overflow-y-auto p-4 outline-none"
        >
          <div className="flex flex-col gap-4 md:hidden">
            {sections.map(renderSection)}
          </div>
          <div className="hidden items-start gap-4 md:flex">
            <div className="flex min-w-0 flex-1 flex-col gap-4">
              {leftSections.map(renderSection)}
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-4">
              {rightSections.map(renderSection)}
            </div>
          </div>
        </div>

        <div className="flex flex-col items-start gap-2 border-t border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-500 dark:border-slate-700 dark:bg-slate-800/80 dark:text-slate-400 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-4">
          <span>Press <Kbd className="text-slate-600 dark:text-slate-300">Esc</Kbd> to close</span>
          <span className="flex items-center gap-1">
            <MagnifyingGlassIcon className="w-4 h-4" /> Shortcuts don’t run while typing in inputs
          </span>
        </div>
      </div>
    </DialogBox>
  );
};

export default ShortcutInformationDialog;
